import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { createApi } from "../../utils/createApi";
import {
  userFacingApiError,
  withAuthRetry,
} from "../../utils/userFacingApiError";
import { WireGuardTunnel } from "./wireGuardTunnelsInterfaces";

interface Props {
  open: boolean;
  selectedTunnel: WireGuardTunnel | null;
  onClose: () => void;
  getGroupName: (groupId: number | null) => string;
  getServerName: (serverId: number | null) => string;
  getServerPublicKey: (serverId: number | null) => string;
  onTunnelPatched: (tunnelId: number) => Promise<void>;
  onTunnelDeleted: (tunnelId: number) => Promise<void>;
  /** ENG-278: false leaves the drawer read-only (no Edit, no Delete). */
  canCreateTunnels: boolean;
}

const Row = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography sx={{ fontFamily: "Mulish", fontSize: 12, color: "#5f6368" }}>
      {label}
    </Typography>
    <Typography
      sx={{ fontFamily: "Mulish", fontSize: 14, overflowWrap: "anywhere" }}
    >
      {value}
    </Typography>
  </Box>
);

const ExpandableHelperText = ({ text }: { text: string }) => {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <span className="font-mulish text-[12px] text-[#6B7280]">
      <span
        style={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: expanded ? "unset" : 1,
          overflow: "hidden",
        }}
      >
        {text}
      </span>
      <button
        type="button"
        className="font-mulish mt-1 p-0 text-[12px] text-[#283E6D] underline"
        onClick={() => setExpanded((prev) => !prev)}
      >
        {expanded ? "Show less" : "Read more"}
      </button>
    </span>
  );
};

export const WireGuardTunnelDetailsDrawer = ({
  open,
  selectedTunnel,
  onClose,
  getGroupName,
  getServerName,
  getServerPublicKey,
  onTunnelPatched,
  onTunnelDeleted,
  canCreateTunnels,
}: Props) => {
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");
  const [editValues, setEditValues] = React.useState({
    name: "",
    serverId: "",
    groupId: "",
    clientPublicKey: "",
    clientTunnelIp: "",
    subnets: "",
    subnetsVirtuallyAssigned: false,
  });

  React.useEffect(() => {
    if (!selectedTunnel) {
      setIsEditMode(false);
      setEditValues({
        name: "",
        serverId: "",
        groupId: "",
        clientPublicKey: "",
        clientTunnelIp: "",
        subnets: "",
        subnetsVirtuallyAssigned: false,
      });
      return;
    }

    setIsEditMode(false);
    setEditValues({
      name: selectedTunnel.name || "",
      serverId:
        selectedTunnel.server !== null && selectedTunnel.server !== undefined
          ? String(selectedTunnel.server)
          : "",
      groupId:
        selectedTunnel.group !== null && selectedTunnel.group !== undefined
          ? String(selectedTunnel.group)
          : "",
      clientPublicKey: selectedTunnel.client_public_key || "",
      clientTunnelIp: selectedTunnel.client_tunnel_ip || "",
      subnets: selectedTunnel.subnets?.join(", ") || "",
      subnetsVirtuallyAssigned: Boolean(
        selectedTunnel.subnets_are_virtually_assigned
      ),
    });
  }, [selectedTunnel]);

  const handleCancelEdit = () => {
    if (!selectedTunnel) {
      setIsEditMode(false);
      return;
    }

    setEditValues({
      name: selectedTunnel.name || "",
      serverId:
        selectedTunnel.server !== null && selectedTunnel.server !== undefined
          ? String(selectedTunnel.server)
          : "",
      groupId:
        selectedTunnel.group !== null && selectedTunnel.group !== undefined
          ? String(selectedTunnel.group)
          : "",
      clientPublicKey: selectedTunnel.client_public_key || "",
      clientTunnelIp: selectedTunnel.client_tunnel_ip || "",
      subnets: selectedTunnel.subnets?.join(", ") || "",
      subnetsVirtuallyAssigned: Boolean(
        selectedTunnel.subnets_are_virtually_assigned
      ),
    });
    setSaveError("");
    setIsEditMode(false);
  };

  const handleDrawerClose = () => {
    setIsEditMode(false);
    setDeleteConfirmOpen(false);
    setSaveError("");
    onClose();
  };

  const handleSaveChanges = async () => {
    if (!selectedTunnel) {
      return;
    }

    const hasRelevantChanges =
      editValues.name !== (selectedTunnel.name || "") ||
      editValues.clientPublicKey !== (selectedTunnel.client_public_key || "");

    if (!hasRelevantChanges) {
      setSaveError("");
      setIsEditMode(false);
      return;
    }

    setIsSaving(true);
    setSaveError("");
    const baseErrorMessage = "Unable to save tunnel changes.";
    try {
      await withAuthRetry(() =>
        createApi("").patch(`wireguard_tunnel/${selectedTunnel.id}/`, {
          name: editValues.name,
          client_public_key: editValues.clientPublicKey,
        })
      );
      await onTunnelPatched(selectedTunnel.id);
      setIsEditMode(false);
    } catch (reason) {
      const apiErrorMessage = userFacingApiError(reason);
      setSaveError(
        apiErrorMessage
          ? `${baseErrorMessage} ${apiErrorMessage}`
          : baseErrorMessage
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTunnel = async () => {
    if (!selectedTunnel) {
      return;
    }

    setIsDeleting(true);
    setSaveError("");
    const baseErrorMessage = "Unable to delete tunnel.";
    try {
      await withAuthRetry(() =>
        createApi("").delete(`wireguard_tunnel/${selectedTunnel.id}/`)
      );
      await onTunnelDeleted(selectedTunnel.id);
      handleDrawerClose();
    } catch (reason) {
      const apiErrorMessage = userFacingApiError(reason);
      setSaveError(
        apiErrorMessage
          ? `${baseErrorMessage} ${apiErrorMessage}`
          : baseErrorMessage
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleDrawerClose}>
      <Box sx={{ width: 420, p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            sx={{ fontFamily: "Mulish", fontSize: 22, fontWeight: 700 }}
          >
            Tunnel Details
          </Typography>
          <Stack direction="row" gap={1}>
            {!isEditMode && canCreateTunnels && (
              <Button
                onClick={() => setIsEditMode(true)}
                startIcon={<EditOutlinedIcon />}
                sx={{ fontFamily: "Mulish", textTransform: "none" }}
                disabled={!selectedTunnel}
              >
                Edit
              </Button>
            )}
            <Button
              onClick={handleDrawerClose}
              sx={{ fontFamily: "Mulish", textTransform: "none" }}
            >
              Close
            </Button>
          </Stack>
        </Stack>
        <Divider sx={{ my: 2 }} />
        {selectedTunnel ? (
          <Stack gap={3}>
            {isEditMode ? (
              <>
                <Row label="Group" value={getGroupName(selectedTunnel.group)} />
                <Row
                  label="WireGuard Server"
                  value={getServerName(selectedTunnel.server)}
                />
                <Row
                  label="Server Public Key"
                  value={getServerPublicKey(selectedTunnel.server)}
                />
                <Row
                  label="Status"
                  value={selectedTunnel.tunnel_status || "unknown"}
                />
                <TextField
                  variant="standard"
                  label="Name"
                  value={editValues.name}
                  onChange={(event) =>
                    setEditValues((prev) => ({
                      ...prev,
                      name: event.target.value,
                    }))
                  }
                  sx={{ "& *": { fontFamily: "Mulish" } }}
                />
                <TextField
                  variant="standard"
                  label="Client Public Key"
                  multiline
                  minRows={2}
                  maxRows={2}
                  value={editValues.clientPublicKey}
                  helperText="WireGuard client public key (must be unique)"
                  onChange={(event) =>
                    setEditValues((prev) => ({
                      ...prev,
                      clientPublicKey: event.target.value,
                    }))
                  }
                  sx={{
                    "& *": { fontFamily: "Mulish" },
                    "& .MuiInputBase-inputMultiline": {
                      overflowWrap: "anywhere",
                      wordBreak: "break-all",
                    },
                  }}
                />
                <Row
                  label="Client Tunnel IP"
                  value={selectedTunnel.client_tunnel_ip || "-"}
                />
                <Row
                  label="Subnets"
                  value={selectedTunnel.subnets?.join(", ") || "-"}
                />
                <Row
                  label="Virtually Assign Subnets"
                  value={
                    selectedTunnel.subnets_are_virtually_assigned ? "Yes" : "No"
                  }
                />
                <Row
                  label="Last Handshake"
                  value={
                    selectedTunnel.last_handshake
                      ? new Date(selectedTunnel.last_handshake).toLocaleString()
                      : "Never"
                  }
                />
                {saveError && (
                  <FormHelperText
                    sx={{ color: "#d32f2f", fontFamily: "Mulish" }}
                  >
                    {saveError}
                  </FormHelperText>
                )}
              </>
            ) : (
              <>
                <Row label="Tunnel Name" value={selectedTunnel.name} />
                <Row label="Group" value={getGroupName(selectedTunnel.group)} />
                <Row
                  label="WireGuard Server"
                  value={getServerName(selectedTunnel.server)}
                />
                <Row
                  label="Server Public Key"
                  value={getServerPublicKey(selectedTunnel.server)}
                />
                <Row
                  label="Status"
                  value={selectedTunnel.tunnel_status || "unknown"}
                />
                <Row
                  label="Client Public Key"
                  value={selectedTunnel.client_public_key || "-"}
                />
                <Row
                  label="Client Tunnel IP"
                  value={selectedTunnel.client_tunnel_ip || "-"}
                />
                <Row
                  label="Subnets"
                  value={selectedTunnel.subnets?.join(", ") || "-"}
                />
                <Row
                  label="Last Handshake"
                  value={
                    selectedTunnel.last_handshake
                      ? new Date(selectedTunnel.last_handshake).toLocaleString()
                      : "Never"
                  }
                />
              </>
            )}
            <Stack direction="row" gap={1} justifyContent="flex-end" pt={2}>
              {!isEditMode && canCreateTunnels && (
                <Button
                  variant="outlined"
                  onClick={() => setDeleteConfirmOpen(true)}
                  disabled={isDeleting}
                  sx={{
                    fontFamily: "Mulish",
                    textTransform: "none",
                    borderColor: "#d32f2f",
                    color: "#d32f2f",
                  }}
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </Button>
              )}
              {isEditMode && (
                <>
                  <Button
                    variant="outlined"
                    onClick={handleCancelEdit}
                    disabled={isSaving || isDeleting}
                    sx={{
                      fontFamily: "Mulish",
                      textTransform: "none",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveChanges}
                    disabled={isSaving || isDeleting}
                    sx={{
                      fontFamily: "Mulish",
                      textTransform: "none",
                      backgroundColor: "#FF8400",
                      "&:hover": { backgroundColor: "#E67300" },
                    }}
                  >
                    {isSaving ? "Saving..." : "Save Changes"}
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
        ) : (
          <Typography sx={{ fontFamily: "Mulish" }}>
            Select a tunnel to view details.
          </Typography>
        )}
      </Box>
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{ sx: { "& *": { fontFamily: "Mulish" } } }}
      >
        <DialogTitle>Delete Tunnel?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: "Mulish" }}>
            Are you sure you want to delete this tunnel?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={isDeleting}
            sx={{ fontFamily: "Mulish", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              await handleDeleteTunnel();
            }}
            disabled={isDeleting}
            sx={{
              fontFamily: "Mulish",
              textTransform: "none",
              color: "#d32f2f",
            }}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};
